import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { logCreation } from '@/lib/activityLog';
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Customer } from "@/types";
import { customerSchema } from "@/lib/validation";
import { ErrorHandler } from "@/lib/errorHandler";
import { getBalanceDisplay } from "@/lib/balanceUtils";
import { useFormSubmitLock } from "@/hooks/useFormSubmitLock";
import { useCustomerGroups } from "@/hooks/useGroupingQueries";
import { ModalHeader } from "@/components/window-manager/ModalHeader";

type CustomerFormData = z.infer<typeof customerSchema>;

interface CustomerModalProps {
  open: boolean;
  isMinimized?: boolean;
  onOpenChange: (open: boolean) => void;
  onMinimize?: () => void;
  onClose?: () => void;
  customer?: Customer;
  organizationId: string | null;
  onSuccess: () => void;
  windowId?: string;
}

export function CustomerModal({ open, isMinimized = false, onOpenChange, onMinimize, onClose, customer, organizationId, onSuccess, windowId }: CustomerModalProps) {
  const { isSubmitting, withSubmitLock } = useFormSubmitLock();
  const { data: customerGroups } = useCustomerGroups();
  
  // Track which windowId this form was initialized for - prevents data loss on minimize/restore
  const initializedWindowIdRef = useRef<string | null>(null);
  
  const form = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      address: "",
      opening_balance: 0,
      balance_type: 'receivable',
      notes: "",
      group_id: "",
    },
  });

  useEffect(() => {
    // Skip if minimized - prevents re-initialization on restore
    if (isMinimized) return;
    // Skip if already initialized for this specific window
    if (windowId && initializedWindowIdRef.current === windowId) return;
    
    if (customer) {
      form.reset({
        name: customer.name,
        phone: customer.phone || "",
        email: customer.email || "",
        address: customer.address || "",
        opening_balance: customer.opening_balance,
        balance_type: (customer as any).balance_type || 'receivable',
        notes: customer.notes || "",
        group_id: (customer as any).group_id || "",
      });
    } else if (open) {
      form.reset({
        name: "",
        phone: "",
        email: "",
        address: "",
        opening_balance: 0,
        balance_type: 'receivable',
        notes: "",
        group_id: "",
      });
    }
    
    if (windowId) {
      initializedWindowIdRef.current = windowId;
    }
  }, [customer, open, form, windowId, isMinimized]);

  const onSubmit = withSubmitLock(async (data: CustomerFormData) => {
    if (!organizationId) {
      toast.error("Organization not found");
      return;
    }

    try {
      // Convert "none" to null for group_id
      const groupId = data.group_id === "none" || !data.group_id ? null : data.group_id;
      
      if (customer) {
        // Update existing customer
        const { error } = await supabase
          .from('customers')
          .update({
            name: data.name,
            phone: data.phone || null,
            email: data.email || null,
            address: data.address || null,
            notes: data.notes || null,
            group_id: groupId,
          })
          .eq('id', customer.id);

        if (error) throw error;
        toast.success('Customer updated successfully');
      } else {
        // For new customers, adjust opening balance based on balance_type
        let adjustedBalance = data.opening_balance || 0;
        if (data.balance_type === 'payable') {
          // "I Owe" - balance should be negative
          adjustedBalance = -Math.abs(adjustedBalance);
        } else {
          // "He Owes" - balance should be positive
          adjustedBalance = Math.abs(adjustedBalance);
        }

        // Create new customer
        const { data: newCustomer, error: insertError } = await supabase
          .from('customers')
          .insert({
            name: data.name,
            phone: data.phone || null,
            email: data.email || null,
            address: data.address || null,
            opening_balance: adjustedBalance,
            current_balance: adjustedBalance,
            balance_type: data.balance_type,
            notes: data.notes || null,
            group_id: groupId,
            organization_id: organizationId,
          })
          .select()
          .single();

        if (insertError) throw insertError;

        if (newCustomer) {
          await logCreation(organizationId, 'customer', newCustomer.id, newCustomer.name);
        }

        // Create opening balance ledger entry if there's an opening balance
        if (adjustedBalance !== 0 && newCustomer) {
          const { error: ledgerError } = await supabase
            .from('ledger_entries')
            .insert({
              organization_id: organizationId,
              party_id: newCustomer.id,
              party_type: 'customer',
              transaction_type: 'opening_balance',
              date: new Date().toISOString().split('T')[0],
              debit: adjustedBalance > 0 ? adjustedBalance : 0,
              credit: adjustedBalance < 0 ? Math.abs(adjustedBalance) : 0,
              balance: adjustedBalance,
              description: 'Opening Balance',
            });

          if (ledgerError) {
            console.error('Error creating opening balance entry:', ledgerError);
            toast.error('Failed to create opening balance ledger entry. Customer creation rolled back.');
            // Delete the customer if ledger entry failed to maintain data integrity
            await supabase.from('customers').delete().eq('id', newCustomer.id);
            throw ledgerError;
          }
        }

        toast.success('Customer created successfully');
      }

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      ErrorHandler.handle(error, customer ? 'Updating Customer' : 'Creating Customer');
    }
  });

  // Handle minimize - keep in taskbar
  const handleMinimize = () => {
    if (onMinimize) {
      onMinimize();
    } else {
      onOpenChange(false);
    }
  };

  // Handle close - remove from taskbar
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} modal={!isMinimized} onOpenChange={(openState) => !openState && handleMinimize()}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto [&>button.absolute]:hidden"
        isMinimized={isMinimized}
        onInteractOutside={(e) => {
          e.preventDefault();
          handleMinimize();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          handleMinimize();
        }}
      >
        <ModalHeader
          title={customer ? 'Edit Customer' : 'Add Customer'}
          onMinimize={handleMinimize}
          onClose={handleClose}
        />
        <DialogDescription>
          {customer ? 'Update customer information' : 'Enter the details of the new customer'}
        </DialogDescription>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter customer name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="+92 300 1234567" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="customer@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location/Address</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter customer address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="group_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer Group</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select group (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {customerGroups?.map((group: any) => (
                        <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!customer && (
              <>
                <FormField
                  control={form.control}
                  name="opening_balance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Opening Balance (PKR)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          step="0.01"
                          placeholder="0" 
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="balance_type"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>Balance Type</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex flex-col space-y-1"
                        >
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="receivable" />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer flex items-center gap-2">
                              <span>He Owes Me</span>
                              <Badge variant="outline" className="bg-[#00c46a]/10 border-[#00c46a]/30">
                                <span className="text-[#00c46a] font-semibold">Receivable</span>
                              </Badge>
                            </FormLabel>
                          </FormItem>
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="payable" />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer flex items-center gap-2">
                              <span>I Owe Him</span>
                              <Badge variant="outline" className="bg-[#ff4d4d]/10 border-[#ff4d4d]/30">
                                <span className="text-[#ff4d4d] font-semibold">Payable</span>
                              </Badge>
                            </FormLabel>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {customer && (
              <div className="p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-muted-foreground">Current Balance:</span>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-foreground">
                      PKR {Math.abs(customer.current_balance || 0).toLocaleString()}
                    </span>
                    <Badge 
                      variant={getBalanceDisplay(customer.current_balance || 0, 'customer').isPositive ? "default" : "destructive"}
                      className={getBalanceDisplay(customer.current_balance || 0, 'customer').bgColor}
                    >
                      <span className={`${getBalanceDisplay(customer.current_balance || 0, 'customer').textColor} font-semibold`}>
                        {getBalanceDisplay(customer.current_balance || 0, 'customer').label}
                      </span>
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Additional notes about the customer" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : (customer ? 'Update' : 'Create')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
